import { type FormEvent, useId, useState } from 'react';

import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';

import type {
  PayrollEmployee,
  PayrollEmployeeCreated,
  PayrollEmployeeUpdate,
  PayrollPasswordIssued,
} from './types';
import { useCreateEmployee, useNewEmployeePassword, useUpdateEmployee } from './usePayroll';

/**
 * A person of the list, new or to change: the name as it goes on the payslip, the NIF or
 * NIE (the back checks its letter), and the address the payslip goes to. The password is
 * never typed here: the platform makes it and shows it once (`PasswordOnce`).
 *
 * New with a NIF already written: «Dar de alta» from a payslip whose NIF is nobody's.
 */
export function EmployeeDialog({
  employee,
  initialNif,
  onCreated,
  onClose,
}: {
  employee?: PayrollEmployee;
  initialNif?: string;
  onCreated?: (created: PayrollEmployeeCreated) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(employee?.full_name ?? '');
  const [nif, setNif] = useState(employee?.nif ?? initialNif ?? '');
  const [email, setEmail] = useState(employee?.email ?? '');
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const newPassword = useNewEmployeePassword();
  const [issued, setIssued] = useState<PayrollPasswordIssued | null>(null);
  const [confirmingPassword, setConfirmingPassword] = useState(false);
  const busy = create.isPending || update.isPending || newPassword.isPending;
  const error = create.error ?? update.error ?? newPassword.error;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!employee) {
      create.mutate(
        { full_name: name.trim(), nif: nif.trim() || null, email: email.trim() || null },
        { onSuccess: (created) => onCreated?.(created) },
      );
      return;
    }
    const payload: PayrollEmployeeUpdate = {};
    if (name.trim() !== employee.full_name) payload.full_name = name.trim();
    // An emptied NIF or address goes as "": the back takes it off.
    if (nif.trim() !== (employee.nif ?? '')) payload.nif = nif.trim();
    if (email.trim().toLowerCase() !== (employee.email ?? '')) payload.email = email.trim();
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    update.mutate({ id: employee.id, payload }, { onSuccess: onClose });
  }

  if (issued) {
    return (
      <PasswordOnce name={issued.full_name} password={issued.password} renewed onClose={onClose} />
    );
  }

  const formId = employee ? `trabajador-${employee.id}` : 'trabajador-nuevo';
  return (
    <Dialog
      title={employee ? employee.full_name : 'Añadir trabajador'}
      description={
        employee
          ? undefined
          : 'La contraseña de su PDF la hace la aplicación y te la enseña una sola vez.'
      }
      onClose={busy ? () => undefined : onClose}
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} disabled={busy || !name.trim()}>
            {busy ? 'Guardando…' : employee ? 'Guardar' : 'Añadir'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label="Nombre y apellidos"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="off"
          hint="Como sale en la nómina: si no lleva NIF, se busca por el nombre."
          required
        />
        <Input
          label="NIF o NIE"
          value={nif}
          onChange={(event) => setNif(event.target.value.toUpperCase())}
          autoComplete="off"
          optional
          hint="Con él se sabe de quién es cada nómina."
        />
        <Input
          label="Correo"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="off"
          optional
          hint="Adonde le llega la suya. Mejor el suyo personal que uno compartido."
        />
        {employee ? (
          <div className="border-line-2 flex flex-col gap-2 border-t pt-3">
            <p className="text-ink-2 text-[13.5px]">
              {employee.has_password
                ? 'Tiene contraseña. Si la ha perdido, hazle una nueva: la anterior deja de abrir las que salgan desde ahora.'
                : 'No tiene contraseña: sin ella no se le puede enviar.'}
            </p>
            {confirmingPassword ? (
              <Callout
                tone="warn"
                compact
                action={
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setConfirmingPassword(false)}
                    >
                      No
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => newPassword.mutate(employee.id, { onSuccess: setIssued })}
                    >
                      Sí, hacer una nueva
                    </Button>
                  </div>
                }
              >
                ¿Hacer una contraseña nueva? Te pedirá la tuya.
              </Callout>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setConfirmingPassword(true)}
                >
                  Nueva contraseña
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    update.mutate(
                      { id: employee.id, payload: { active: !employee.active } },
                      { onSuccess: onClose },
                    )
                  }
                >
                  {employee.active ? 'Dar de baja' : 'Volver a dar de alta'}
                </Button>
              </div>
            )}
          </div>
        ) : null}
        {error ? <ErrorMessage error={error}>No se pudo guardar</ErrorMessage> : null}
      </form>
    </Dialog>
  );
}

/**
 * A password the platform just made, shown this once: the API does not give it again
 * (a lost one is a new one). Copied with a button; the text stays selectable for when
 * the clipboard is not there.
 */
export function PasswordOnce({
  name,
  password,
  renewed = false,
  onClose,
}: {
  name: string;
  password: string;
  renewed?: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const passwordId = useId();

  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Dialog
      title={`Contraseña de ${name}`}
      size="sm"
      onClose={onClose}
      // Only «Ya la he apuntado» closes it: Escape, «×» or a click outside would lose it for
      // good.
      busy
      footer={
        <>
          <Button variant="secondary" onClick={() => void copy()}>
            {copied ? 'Copiada' : 'Copiar'}
          </Button>
          <Button onClick={onClose} data-autofocus>
            Ya la he apuntado
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-1">
        <label htmlFor={passwordId} className="text-ink-3 text-[12.5px]">
          Contraseña
        </label>
        <output id={passwordId} className="text-ink font-mono text-[22px] tracking-wide select-all">
          {password}
        </output>
      </div>
      <p className="text-ink-2 mt-3 text-[13.5px]">
        Apúntala y dásela a {name}: con ella abre el PDF de su nómina.{' '}
        <strong>No se vuelve a enseñar.</strong> Vale para todos los meses
        {renewed ? '; la anterior ya no abre las que salgan desde ahora' : ''}.
      </p>
    </Dialog>
  );
}
